import type { LocalDict } from "../../core/i18n";

// Chat view translations. Shared keys (nav.*, chat.*, common.you) resolve via the
// useT fallback into the central dictionary, so only chat-local strings live here.
export const STR: LocalDict = {
  en: {
    "agent.fallback": "agent",
    "subagent.fallback": "subagent",
    "thread.fallback": "Conversation",
    "thread.origin.scheduled": "Scheduled",
    "thread.origin.heartbeat": "Heartbeat",
    "thread.origin.peer": "Agent",
    "thread.origin.autonomous": "Autonomous",
    "thread.origin.cortex": "Cortex",
    "thread.origin.external": "External",
    "thread.source.dashboard": "Dashboard",
    "thread.source.scheduler": "Scheduler",
    "thread.source.cli": "Terminal",
    "thread.source.webhook": "Webhook",
    "thread.channel.via": "Arrived via {channel}",
    "thread.empty": "No conversations yet.",
    "thread.loading": "Opening your latest conversation…",
    // ── chat source filter (the simple rail, its all-chats panel, the
    //    dashboard sidebar's per-agent list — views/chat/ThreadFilterChips.tsx)
    "filter.label": "Show chats from",
    "filter.all": "All",
    "filter.allHelp": "Show every chat, wherever it came from.",
    "filter.chats": "Chats",
    "filter.chatsHelp":
      "Only conversations typed here or in the dashboard — hides scheduled runs, other agents' conversations and messaging apps.",
    "filter.scheduled": "Scheduled",
    "filter.scheduledHelp":
      "Only runs the agent's schedule started on its own. To hide them, pick any other filter.",
    "filter.agents": "Agents",
    "filter.agentsHelp":
      "Only conversations another agent started with this one, plus the agent's own background work.",
    "filter.platformHelp": "Only conversations that arrived via {channel}.",
    "filter.help":
      "Each chat counts under exactly one source — a run the schedule started files under Scheduled even when it was delivered on a messaging app. “All” shows everything; the choice is remembered per agent on this device.",
    "thread.msg": "msg",
    "thread.msgs": "msgs",
    "thread.delete.aria": "Delete conversation",
    "thread.delete.confirm": "Delete this conversation?",
    "thread.rename.aria": "Rename conversation",
    "thread.rename.placeholder": "Name this conversation…",
    "thread.edit": "Edit",
    "thread.done": "Done",
    "thread.selectAll": "Select all",
    "thread.deselectAll": "Deselect all",
    "thread.deleteSelected.confirm": "Delete {n} selected conversations?",
    "thread.deleteBtn": "Delete",
    "thread.cancel": "Cancel",
    "thread.count.one": "{n} conversation",
    "thread.count.many": "{n} conversations",
    "thread.streaming": "Agent is working…",
    "thread.unread": "{n} new from the agent",
    "resize.title": "Drag to resize · double-click to reset",
    "addbot.chip": "Add an agent",
    "addbot.chipHelp":
      "Bring another agent into this conversation. It becomes a group chat: this agent stays the one that answers by default, the other agents join in when you @mention them, and the recent history comes along so nobody has to be caught up by hand.",
    "history.show": "Show conversation history",
    "history.hide": "Hide conversation history",
    "history.title": "Conversations",
    "history.close": "Close conversation history",
    "profile.show": "Show agent profile",
    "profile.hide": "Hide agent profile",
    "compose.send": "Send",
    "compose.stop": "Stop",
    "compose.queue": "Queue",
    "compose.queueTitle":
      "Queue: sends this after the current reply finishes (Enter does the same on a keyboard). Use Steer to hand it to the agent now instead.",
    "compose.steer": "Steer",
    "compose.steerTitle":
      "Steer: hand this to the agent while it is still replying. It reads it at its next step, after the tool it is running finishes, without stopping. If the reply finishes first, it is sent as your next message.",
    "queue.label": "{n} waiting",
    "queue.kind.queue": "Sends after this reply",
    "queue.kind.steer": "The agent reads this at its next step, without stopping",
    "queue.steer":
      "Steer: the agent reads this at its next step without stopping. If the reply finishes first, it is sent as your next message.",
    "queue.steerAttachments": "Messages with files can't be steered — this one is sent after the reply",
    "queue.steerIdle": "Nothing is running — this is sent as the next message",
    "queue.steered": "Steered — waiting for the agent's next step",
    "queue.edit": "Edit: take this back into the message box",
    "queue.cancel": "Cancel: don't send this",
    "queue.clear": "Cancel all",
    "msg.steered": "Steered in",
    "msg.steeredTitle": "Sent while the agent was working — it read this at its next step, mid-reply",
    "chat.thinking": "Thinking…",
    "chat.remote.working": "Working on this from {channel}…",
    "chat.remote.workingUnknown": "Working on this from another device…",
    "chat.jumpLatest": "Jump to latest",
    "toast.threadsFailed": "Could not load the conversation list.",
    "msg.copy": "Copy",
    "msg.copied": "Copied",
    "msg.copyFailed": "Could not copy the message.",
    "msg.edit": "Edit",
    "msg.editSend": "Send",
    "msg.editCancel": "Cancel",
    "msg.time.now": "just now",
    "msg.time.min.one": "{n} min ago",
    "msg.time.min.many": "{n} mins ago",
    "msg.time.hour.one": "{n} hour ago",
    "msg.time.hour.many": "{n} hours ago",
    "error.label": "Error",
    "error.fix": "Fix",
    "error.askAdmin": "This needs a key or sign-in that only your workspace admin can set up. Ask them to connect it — once they do, just send your message again.",
    "error.openDashboard": "Open the full dashboard to fix this",
    "error.newChat": "Start a new chat",
    "error.newChatHint": "This conversation cannot continue with the model it is set to. Start a new chat with this agent, or switch it back to the model it was using before.",
    "error.codexName": "OpenAI Codex",
    "shell.placeholder": "Run a shell command…  (starts with !)",
    "shell.mode": "Shell mode — runs in {agent}'s workspace, not sent to the agent",
    "shell.running": "Running…",
    "shell.error": "Shell command failed: {msg}",
    "shell.queued": "{n} shell result(s) — sends with your next message",
    "shell.sendNow": "Send to Agent now",
    "shell.clear": "Clear",
    "time.now": "now",
    "time.minutes": "{n}m",
    "time.hours": "{n}h",
    "time.days": "{n}d",
    "mode.title":
      "Approval for this session — overrides the agent's Oversight profile. Auto: approve everything · Plan: draft first, approve steps · Manual: ask before each gated tool · Act: execute now, approve all. Click to cycle.",
    "mode.auto": "Auto",
    "mode.plan": "Plan",
    "mode.manual": "Manual",
    "detail.title":
      "Tool & subagent detail — Quiet hides tool calls, Normal shows an activity line, Verbose shows full input/output. Click to cycle.",
    "detail.quiet": "Quiet",
    "detail.normal": "Normal",
    "detail.verbose": "Verbose",
    "model.title": "Model for this chat — click to change",
    "model.none": "Model",
    "model.default": "Agent default",
    "model.search": "Search models…",
    "model.empty": "No models available",
    "model.unavailable":
      "This conversation last ran on {model}, which is no longer available here. Continuing on the agent's default model.",
    "effort.title": "Reasoning effort for this chat — click to change",
    "effort.chip": "Effort",
    "effort.defaultLevel": "Agent default ({level})",
    "effort.modelDefault": "Model default",
    "context.title": "{used} / {total} tokens in context",
    "tokens.title": "Input ↑ / output ↓ tokens (this conversation)",
    "calls.title": "Model calls (this conversation) — one per model round-trip, tool loops included",
    "toolCalls.title": "Tool calls (this conversation), including MCP tools",
    "cost.title": "Estimated cost (this conversation)",
    "cost.credits.title": "Olano credits used (this conversation) \u2014 charged by the Olano gateway",
    "approval.needed": "Approval needed",
    "approval.blocked.one": "{n} action blocked",
    "approval.blocked.many": "{n} actions blocked",
    "approval.acceptOnce": "Accept once",
    "approval.browserSendsTo": "The form is sent to {site}.",
    "approval.browserNote": "Approve only if you asked for this: a web page can try to steer an agent.",
    "browser.handedBack": "Done: I've handed the browser back to you.",
    "approval.acceptAll": "Accept all",
    "approval.reject": "Reject",
    "approval.accepted": "Accepted",
    "approval.acceptedAll": "Accepted (all for this turn)",
    "approval.rejected": "Rejected",
    "approval.autoApproved": "Auto-approved",
    "approval.failed": "Could not submit approval.",
    "approval.expired": "This approval expired while you were away",
    "interrupted.chip": "Interrupted",
    "interrupted.line": "Connection lost while {agent} was replying — the reply below may be incomplete.",
    "interrupted.checking": "Checking for the full reply…",
    "interrupted.retry": "Retry",
    "interrupted.retryTitle": "Send your last message again",
    "interrupted.recovered": "Recovered the full reply",
    // The turn is still running on the server; this tab is catching up on
    // what it missed. Deliberately not "Interrupted" — nothing was lost.
    "reattach.chip": "Reconnecting",
    "reattach.line": "{agent} kept working while you were away — catching up on the reply.",
    "mode.set": "Mode set to {mode}.",
    "plan.approveRun": "Approve & run",
    "plan.approveMsg": "Approved — execute the plan now.",
    "mode.act": "Act",
    "slash.act": "Act mode — execute now, approve all tools",
    "detail.set": "Detail set to {state}.",
    "activity.subagent": "Talking with {name}",
    "activity.tool": "Calling {name}",
    "todo.title": "Plan",
    "todo.progress": "{done}/{total} done",
    "slash.heading": "Slash commands",
    "slash.unknown": "Unknown command: /{name}. Type /help.",
    "slash.help": "Show available commands",
    "slash.new": "Start a new conversation",
    "slash.context": "Show context & token usage",
    "slash.compact": "Summarise & compact this thread",
    "slash.quiet": "Hide all tool calls",
    "slash.normal": "Show a tool activity line (default)",
    "slash.verbose": "Show full tool input & output",
    "slash.auto": "Auto mode — run tools without asking",
    "slash.plan": "Plan mode — confirm before executing",
    "slash.manual": "Manual mode — approve every tool call",
    "slash.rename": "Rename the current thread",
    "slash.skills": "List this agent's skills",
    "slash.subagents": "List this agent's subagents",
    "slash.tools": "List this agent's tools",
    "slash.mcps": "List this agent's MCP servers",
    "slash.reloadSkills": "Reload skills from disk",
    "slash.reloadTools": "Reload the tool catalog",
    "slash.reloadMcps": "Reload MCP servers",
    "slash.reloadAgent": "Reload agent config from disk + rebuild",
    "slash.reloadVault": "Re-read vault secrets + rebuild agent",
    "slash.group.commands": "Commands",
    "slash.group.skills": "Skills",
    "slash.customCommand": "Custom command",
    "slash.skills.title": "Skills available to {agent}",
    "slash.skills.none": "No skills available.",
    "slash.skills.hint": "Type `/<skill> [task]` to invoke one.",
    "slash.subagents.title": "{agent}'s subagents",
    "slash.subagents.none": "This agent has no subagents.",
    "slash.tools.title": "Tools available to {agent}",
    "slash.tools.none": "No tools configured.",
    "slash.mcps.title": "{agent}'s MCP servers",
    "slash.mcps.none": "This agent has no MCP servers.",
    "slash.mcps.disabled": "disabled",
    "slash.mcps.status.connected": "connected",
    "slash.mcps.status.authRequired": "auth required",
    "slash.mcps.status.error": "error",
    "slash.mcps.tool": "{n} tool",
    "slash.mcps.tools": "{n} tools",
    "slash.mcps.notRunning": "Agent not running — status reflects config + stored tokens.",
    "mcp.statusTitle": "MCP server status — click for details",
    "mcp.badge": "MCP {live}/{total}",
    "mcp.heading": "MCP servers",
    "mcp.setup": "Setup →",
    "mcp.noServers": "No MCP servers.",
    "reload.running": "Reloading…",
    "reload.failed": "Reload failed.",
    "reload.skills.done": "Reloaded {n} skill(s).",
    "reload.tools.done": "Reloaded tools — {n} on this agent.",
    "reload.mcps.done": "Reloaded {n} MCP server(s).",
    "reload.agent.done": "Reloaded {agent} from disk — config changes applied.",
    "reload.vault.done": "Vault reloaded — {n} secret(s) available; {agent} rebuilt.",
    "reload.vault.locked": "The vault is locked. Unlock it, then reload.",
    "context.heading": "Context usage",
    "context.model": "Model",
    "context.window": "Window",
    "context.estimated": "Estimated usage by category",
    "context.usage": "{used} / {total} tokens ({pct}%)",
    "context.usageNoWindow": "{used} tokens",
    "context.tokens": "tokens",
    "context.items": "{n} items",
    "context.onDemand": "loaded on-demand",
    "context.cat.system_prompt": "System prompt",
    "context.cat.system_tools": "System tools",
    "context.cat.subagents": "Custom agents",
    "context.cat.memory_files": "Memory files",
    "context.cat.skills": "Skills",
    "context.cat.mcp_tools": "MCP tools",
    "context.cat.messages": "Messages",
    "context.cat.free": "Free space",
    "compact.noThread": "No active thread to compact.",
    "compact.running": "Compacting conversation…",
    "compact.done": "Conversation compacted.",
    "compact.empty": "Nothing to compact yet.",
    "compact.failed": "Could not compact the conversation.",
    "rename.noThread": "No active thread to rename.",
    "rename.usage": "Usage: /rename <new name>",
    "rename.done": "Renamed to \"{name}\".",
    "rename.failed": "Could not rename the thread.",
    "att.attach": "Attach files",
    "att.drop": "Drop to attach",
    "att.tooLarge": "{name} is larger than 10 MB.",
    "att.unreadable": "{name}: the file could not be read. Try saving it to your device first.",
    "att.remove": "Remove attachment",
    "att.download": "Download",
    "att.openInFiles": "Open in Files",
    "voice.hold": "Hold to dictate · ⌘D / Ctrl+D to toggle",
    "voice.stop": "Stop dictating",
    "voice.listening": "Listening…",
    "voice.listeningHint": "release, or press ⌘D / Ctrl+D, to stop",
    "voice.transcribing": "Transcribing…",
    "voice.denied": "Microphone access was blocked — allow it in your browser's site settings.",
    "voice.noSpeech": "Didn't catch that — try again.",
    "voice.network": "Speech recognition is offline — check your connection.",
    "voice.sttOff": "Transcription is turned off — enable it in Config → Speech.",
    "voice.error": "Voice input failed.",
    "toast.agentsFailed": "Could not load agents.",
    "toast.openFailed": "Could not open thread.",
    "toast.deleteFailed": "Could not delete thread.",
    "toast.chatFailed": "Chat failed.",
    "connect.title": "Connect to {service}",
    "connect.magicTitle": "Connect your account",
    "connect.button": "Connect",
    "connect.pin": "PIN",
    "connect.hint": "Opens in your browser — the connection completes here on its own after you sign in.",
    "result.doneTitle": "Connected",
    "result.failedTitle": "Didn't complete",
    "result.restarted": "Restarted {agent} so the change is live.",
    "result.reloaded": "Reloaded {agent}'s tools, so they're usable now.",
    "result.restartNeeded": "{agent} needs a restart to pick this up.",
    "result.restartBtn": "Restart {agent}",
    "result.restarting": "Restarting…",
    "result.restartDone": "{agent} restarted.",
    "result.restartFailed": "Couldn't restart {agent}. Try again from the agent's page.",
    "result.restartAsk": "Ask an admin to restart {agent} so it picks this up.",
    "secret.loading": "Preparing the secure form…",
    "secret.save": "Save to vault",
    "secret.saving": "Saving…",
    "secret.savedTitle": "Saved to the vault",
    "secret.hint": "Goes straight into the encrypted vault, never through this chat. The agent only learns the name.",
    "secret.spent": "This request is no longer active. Ask the agent again if you still need to add it.",
    "secret.failed": "Couldn't save. {detail}",
  },
  zh: {
    "agent.fallback": "智能体",
    "subagent.fallback": "子智能体",
    "thread.fallback": "对话",
    "thread.origin.scheduled": "定时",
    "thread.origin.heartbeat": "心跳",
    "thread.origin.peer": "智能体",
    "thread.origin.autonomous": "自主",
    "thread.origin.cortex": "认知",
    "thread.origin.external": "外部",
    "thread.source.dashboard": "仪表板",
    "thread.source.scheduler": "调度器",
    "thread.source.cli": "终端",
    "thread.source.webhook": "Webhook",
    "thread.channel.via": "来自 {channel}",
    "thread.empty": "暂无对话。",
    "thread.loading": "正在打开最近的对话…",
    "filter.label": "按来源筛选对话",
    "filter.all": "全部",
    "filter.allHelp": "显示所有对话，不论来源。",
    "filter.chats": "对话",
    "filter.chatsHelp": "仅显示在此处或仪表板中输入的对话——隐藏定时任务、其他智能体的会话和消息应用。",
    "filter.scheduled": "定时",
    "filter.scheduledHelp": "仅显示由智能体的定时任务自行启动的运行。要隐藏它们，选择其他任意筛选即可。",
    "filter.agents": "智能体",
    "filter.agentsHelp": "仅显示其他智能体与它发起的会话，以及它自己的后台工作。",
    "filter.platformHelp": "仅显示通过 {channel} 收到的对话。",
    "filter.help":
      "每个对话只归入一个来源——定时任务发起的运行即使通过消息应用送达也归入“定时”。选择“全部”可查看所有对话；该选择按智能体保存在本设备上。",
    "thread.msg": "条消息",
    "thread.msgs": "条消息",
    "thread.delete.aria": "删除对话",
    "thread.delete.confirm": "确定删除这个对话吗？",
    "thread.rename.aria": "重命名对话",
    "thread.rename.placeholder": "为此对话命名…",
    "thread.edit": "编辑",
    "thread.done": "完成",
    "thread.selectAll": "全选",
    "thread.deselectAll": "取消全选",
    "thread.deleteSelected.confirm": "删除选中的 {n} 个对话？",
    "thread.deleteBtn": "删除",
    "thread.cancel": "取消",
    "thread.count.one": "{n} 个对话",
    "thread.count.many": "{n} 个对话",
    "thread.streaming": "智能体正在处理…",
    "resize.title": "拖动以调整宽度 · 双击重置",
    "addbot.chip": "添加智能体",
    "addbot.chipHelp":
      "把另一个智能体拉进这段对话，它会变成群聊：当前智能体仍是默认回复者，其他智能体在你 @提及 时参与，最近的聊天记录会一并带过去，无需手动复述。",
    "history.show": "显示对话历史",
    "history.hide": "隐藏对话历史",
    "history.title": "对话列表",
    "history.close": "关闭对话历史",
    "profile.show": "查看智能体资料",
    "profile.hide": "隐藏智能体资料",
    "thread.unread": "智能体有 {n} 条新消息",
    "compose.send": "发送",
    "compose.stop": "停止",
    "compose.queue": "排队",
    "compose.queueTitle": "排队：在当前回复结束后发送（键盘上按 Enter 效果相同）。想让智能体现在就读到，请用“插话”。",
    "compose.steer": "插话",
    "compose.steerTitle":
      "插话：在智能体仍在回复时把这条消息交给它。它会在下一步（当前工具运行结束后）读取，不会中断。如果回复先结束，这条消息会作为你的下一条消息发送。",
    "queue.label": "{n} 条等待中",
    "queue.kind.queue": "在本次回复后发送",
    "queue.kind.steer": "智能体将在下一步读取，不会中断",
    "queue.steer": "插话：智能体将在下一步读取，不会中断。如果回复先结束，会作为你的下一条消息发送。",
    "queue.steerAttachments": "带文件的消息无法插话——将在回复结束后发送",
    "queue.steerIdle": "当前没有正在运行的回复——将作为下一条消息发送",
    "queue.steered": "已插话——等待智能体的下一步",
    "queue.edit": "编辑：把这条消息放回输入框",
    "queue.cancel": "取消：不发送这条消息",
    "queue.clear": "全部取消",
    "msg.steered": "插话",
    "msg.steeredTitle": "在智能体工作时发送——它在下一步（回复中途）读取了这条消息",
    "chat.thinking": "思考中…",
    "chat.remote.working": "正在通过 {channel} 处理…",
    "chat.remote.workingUnknown": "正在从另一台设备处理…",
    "chat.jumpLatest": "跳到最新",
    "toast.threadsFailed": "无法加载会话列表。",
    "msg.copy": "复制",
    "msg.copied": "已复制",
    "msg.copyFailed": "无法复制该消息。",
    "msg.edit": "编辑",
    "msg.editSend": "发送",
    "msg.editCancel": "取消",
    "msg.time.now": "刚刚",
    "msg.time.min.one": "{n} 分钟前",
    "msg.time.min.many": "{n} 分钟前",
    "msg.time.hour.one": "{n} 小时前",
    "msg.time.hour.many": "{n} 小时前",
    "error.label": "错误",
    "error.fix": "修复",
    "error.askAdmin": "这需要只有工作区管理员才能设置的密钥或登录。请联系管理员完成连接 —— 完成后重新发送消息即可。",
    "error.openDashboard": "打开完整仪表板来修复",
    "error.newChat": "开始新对话",
    "error.newChatHint": "当前模型无法继续这段对话。请与该机器人开始新对话，或将其切换回之前使用的模型。",
    "error.codexName": "OpenAI Codex",
    "shell.placeholder": "运行 Shell 命令…（以 ! 开头）",
    "shell.mode": "Shell 模式 — 在 {agent} 的工作区运行，不会发送给智能体",
    "shell.running": "运行中…",
    "shell.error": "Shell 命令失败：{msg}",
    "shell.queued": "{n} 条 Shell 结果 — 将随下一条消息发送",
    "shell.sendNow": "立即发送给智能体",
    "shell.clear": "清除",
    "time.now": "刚刚",
    "time.minutes": "{n} 分钟前",
    "time.hours": "{n} 小时前",
    "time.days": "{n} 天前",
    "mode.title":
      "本次会话的审批方式 — 覆盖智能体的监管模式。自动：批准一切 · 计划：先拟方案再逐步批准 · 手动：每个受控工具都询问。点击切换。",
    "mode.auto": "自动",
    "mode.plan": "计划",
    "mode.manual": "手动",
    "detail.title": "工具与子智能体详情 — 安静：隐藏所有工具调用；普通：显示活动行；详细：显示完整输入/输出。点击切换。",
    "detail.quiet": "安静",
    "detail.normal": "普通",
    "detail.verbose": "详细",
    "model.title": "本次对话的模型 — 点击切换",
    "model.none": "模型",
    "model.default": "智能体默认",
    "model.search": "搜索模型…",
    "model.empty": "暂无可用模型",
    "model.unavailable": "本次对话上次使用的模型 {model} 在此处已不可用。将改用智能体的默认模型继续。",
    "effort.title": "本次对话的推理强度 — 点击切换",
    "effort.chip": "推理强度",
    "effort.defaultLevel": "智能体默认（{level}）",
    "effort.modelDefault": "模型默认",
    "context.title": "上下文 {used} / {total} 个 token",
    "tokens.title": "输入 ↑ / 输出 ↓ token（本次对话）",
    "calls.title": "模型调用（本次对话）— 每次模型往返计一次，含工具循环",
    "toolCalls.title": "工具调用（本次对话），含 MCP 工具",
    "cost.title": "预计费用（本次对话）",
    "cost.credits.title": "本次对话已使用的 Olano 积分 \u2014 由 Olano 网关计费",
    "approval.needed": "需要审批",
    "approval.blocked.one": "{n} 个操作待批",
    "approval.blocked.many": "{n} 个操作待批",
    "approval.acceptOnce": "批准一次",
    "approval.browserSendsTo": "表单将发送到 {site}。",
    "approval.browserNote": "只有在你确实要求这样做时才批准：网页可能会试图操纵智能体。",
    "browser.handedBack": "好了，我已把浏览器交还给你。",
    "approval.acceptAll": "全部批准",
    "approval.reject": "拒绝",
    "approval.accepted": "已批准",
    "approval.acceptedAll": "已批准（本轮全部）",
    "approval.rejected": "已拒绝",
    "approval.autoApproved": "已自动批准",
    "approval.failed": "无法提交审批。",
    "approval.expired": "此审批在您离开时已过期",
    "interrupted.chip": "已中断",
    "interrupted.line": "{agent} 回复时连接中断——下面的回复可能不完整。",
    "interrupted.checking": "正在检查完整回复…",
    "interrupted.retry": "重试",
    "interrupted.retryTitle": "重新发送上一条消息",
    "interrupted.recovered": "已恢复完整回复",
    "reattach.chip": "正在重新连接",
    "reattach.line": "您离开时 {agent} 仍在继续处理——正在同步回复。",
    "mode.set": "模式已设为 {mode}。",
    "plan.approveRun": "批准并执行",
    "plan.approveMsg": "已批准 — 现在执行该计划。",
    "mode.act": "执行",
    "slash.act": "执行模式 — 立即执行并批准所有工具",
    "detail.set": "详情已设为{state}。",
    "activity.subagent": "正在与 {name} 协作",
    "activity.tool": "正在调用 {name}",
    "todo.title": "计划",
    "todo.progress": "完成 {done}/{total}",
    "slash.heading": "斜杠命令",
    "slash.unknown": "未知命令：/{name}。输入 /help 查看。",
    "slash.help": "显示可用命令",
    "slash.new": "开始新对话",
    "slash.context": "显示上下文与 token 用量",
    "slash.compact": "总结并压缩此对话",
    "slash.quiet": "隐藏所有工具调用",
    "slash.normal": "显示工具活动行（默认）",
    "slash.verbose": "显示完整的工具输入与输出",
    "slash.auto": "自动模式——无需询问直接执行",
    "slash.plan": "计划模式——执行前先确认",
    "slash.manual": "手动模式——每次工具调用都需批准",
    "slash.rename": "重命名当前对话",
    "slash.skills": "列出该智能体的技能",
    "slash.subagents": "列出该智能体的子智能体",
    "slash.tools": "列出该智能体的工具",
    "slash.mcps": "列出该智能体的 MCP 服务器",
    "slash.reloadSkills": "从磁盘重新加载技能",
    "slash.reloadTools": "重新加载工具目录",
    "slash.reloadMcps": "重新加载 MCP 服务器",
    "slash.reloadAgent": "从磁盘重新加载智能体配置并重建",
    "slash.reloadVault": "重新读取保险库密钥并重建智能体",
    "slash.group.commands": "命令",
    "slash.group.skills": "技能",
    "slash.customCommand": "自定义命令",
    "slash.skills.title": "{agent} 可用的技能",
    "slash.skills.none": "暂无可用技能。",
    "slash.skills.hint": "输入 `/<技能> [任务]` 即可调用。",
    "slash.subagents.title": "{agent} 的子智能体",
    "slash.subagents.none": "该智能体没有子智能体。",
    "slash.tools.title": "{agent} 可用的工具",
    "slash.tools.none": "未配置任何工具。",
    "slash.mcps.title": "{agent} 的 MCP 服务器",
    "slash.mcps.none": "该智能体没有 MCP 服务器。",
    "slash.mcps.disabled": "已禁用",
    "slash.mcps.status.connected": "已连接",
    "slash.mcps.status.authRequired": "需要授权",
    "slash.mcps.status.error": "错误",
    "slash.mcps.tool": "{n} 个工具",
    "slash.mcps.tools": "{n} 个工具",
    "slash.mcps.notRunning": "智能体未运行 — 状态基于配置与已存令牌。",
    "mcp.statusTitle": "MCP 服务器状态 — 点击查看详情",
    "mcp.badge": "MCP {live}/{total}",
    "mcp.heading": "MCP 服务器",
    "mcp.setup": "设置 →",
    "mcp.noServers": "暂无 MCP 服务器。",
    "reload.running": "重新加载中……",
    "reload.failed": "重新加载失败。",
    "reload.skills.done": "已重新加载 {n} 个技能。",
    "reload.tools.done": "已重新加载工具——该智能体有 {n} 个。",
    "reload.mcps.done": "已重新加载 {n} 个 MCP 服务器。",
    "reload.agent.done": "已从磁盘重新加载 {agent}——配置更改已应用。",
    "reload.vault.done": "保险库已重新加载——{n} 个密钥可用；{agent} 已重建。",
    "reload.vault.locked": "保险库已锁定。请先解锁，然后重新加载。",
    "context.heading": "上下文用量",
    "context.model": "模型",
    "context.window": "窗口",
    "context.estimated": "按类别估算的用量",
    "context.usage": "{used} / {total} tokens（{pct}%）",
    "context.usageNoWindow": "{used} tokens",
    "context.tokens": "tokens",
    "context.items": "{n} 项",
    "context.onDemand": "按需加载",
    "context.cat.system_prompt": "系统提示",
    "context.cat.system_tools": "系统工具",
    "context.cat.subagents": "自定义智能体",
    "context.cat.memory_files": "记忆文件",
    "context.cat.skills": "技能",
    "context.cat.mcp_tools": "MCP 工具",
    "context.cat.messages": "消息",
    "context.cat.free": "剩余空间",
    "compact.noThread": "没有可压缩的活动对话。",
    "compact.running": "正在压缩对话……",
    "compact.done": "对话已压缩。",
    "compact.empty": "暂无可压缩内容。",
    "compact.failed": "无法压缩对话。",
    "rename.noThread": "没有可重命名的活动对话。",
    "rename.usage": "用法：/rename <新名称>",
    "rename.done": "已重命名为“{name}”。",
    "rename.failed": "无法重命名对话。",
    "att.attach": "添加附件",
    "att.drop": "拖放以添加附件",
    "att.tooLarge": "{name} 超过 10 MB。",
    "att.unreadable": "{name}：无法读取该文件。请先将其保存到设备上再试。",
    "att.remove": "移除附件",
    "att.download": "下载",
    "att.openInFiles": "在文件中打开",
    "voice.hold": "按住即可语音输入 · ⌘D / Ctrl+D 切换",
    "voice.stop": "停止语音输入",
    "voice.listening": "正在聆听…",
    "voice.listeningHint": "松开或按 ⌘D / Ctrl+D 结束",
    "voice.transcribing": "正在转写…",
    "voice.denied": "麦克风权限被拒绝 — 请在浏览器的网站设置中允许。",
    "voice.noSpeech": "没有听清 — 请再试一次。",
    "voice.network": "语音识别服务不可用 — 请检查网络连接。",
    "voice.sttOff": "转写功能已关闭 — 请在「配置 → 语音」中启用。",
    "voice.error": "语音输入失败。",
    "toast.agentsFailed": "无法加载智能体。",
    "toast.openFailed": "无法打开对话。",
    "toast.deleteFailed": "无法删除对话。",
    "toast.chatFailed": "对话失败。",
    "connect.title": "连接 {service}",
    "connect.magicTitle": "连接你的账号",
    "connect.button": "连接",
    "connect.pin": "PIN 码",
    "connect.hint": "将在浏览器中打开 — 登录完成后,连接会自动在这里完成。",
    "result.doneTitle": "已连接",
    "result.failedTitle": "未能完成",
    "result.restarted": "已重启 {agent}，更改已生效。",
    "result.reloaded": "已重新加载 {agent} 的工具，现在即可使用。",
    "result.restartNeeded": "需要重启 {agent} 才能使其生效。",
    "result.restartBtn": "重启 {agent}",
    "result.restarting": "正在重启……",
    "result.restartDone": "{agent} 已重启。",
    "result.restartFailed": "无法重启 {agent}。请在该智能体页面重试。",
    "result.restartAsk": "请管理员重启 {agent} 以使其生效。",
    "secret.loading": "正在准备安全表单……",
    "secret.save": "保存到保险库",
    "secret.saving": "正在保存……",
    "secret.savedTitle": "已保存到保险库",
    "secret.hint": "直接存入加密保险库，不经过此聊天。智能体只会知道它的名称。",
    "secret.spent": "此请求已失效。如仍需添加，请再次询问智能体。",
    "secret.failed": "无法保存。{detail}",
  },
  es: {
    "agent.fallback": "agente",
    "subagent.fallback": "subagente",
    "thread.fallback": "Conversación",
    "thread.origin.scheduled": "Programado",
    "thread.origin.heartbeat": "Latido",
    "thread.origin.peer": "Agente",
    "thread.origin.autonomous": "Autónomo",
    "thread.origin.cortex": "Cortex",
    "thread.origin.external": "Externo",
    "thread.source.dashboard": "Panel",
    "thread.source.scheduler": "Programador",
    "thread.source.cli": "Terminal",
    "thread.source.webhook": "Webhook",
    "thread.channel.via": "Recibido por {channel}",
    "thread.empty": "Aún no hay conversaciones.",
    "thread.loading": "Abriendo tu conversación más reciente…",
    "filter.label": "Mostrar chats de",
    "filter.all": "Todos",
    "filter.allHelp": "Muestra todos los chats, vengan de donde vengan.",
    "filter.chats": "Chats",
    "filter.chatsHelp":
      "Solo conversaciones escritas aquí o en el panel — oculta las ejecuciones programadas, las conversaciones de otros agentes y las apps de mensajería.",
    "filter.scheduled": "Programados",
    "filter.scheduledHelp":
      "Solo ejecuciones que la programación del agente inició por su cuenta. Para ocultarlas, elige cualquier otro filtro.",
    "filter.agents": "Agentes",
    "filter.agentsHelp":
      "Solo conversaciones que otro agente inició con este, más el trabajo en segundo plano del propio agente.",
    "filter.platformHelp": "Solo conversaciones llegadas por {channel}.",
    "filter.help":
      "Cada chat cuenta en una sola fuente — una ejecución programada aparece bajo Programados aunque se entregara por una app de mensajería. «Todos» muestra todo; la elección se recuerda por agente en este dispositivo.",
    "thread.msg": "mensaje",
    "thread.msgs": "mensajes",
    "thread.delete.aria": "Eliminar conversación",
    "thread.delete.confirm": "¿Eliminar esta conversación?",
    "thread.rename.aria": "Renombrar conversación",
    "thread.rename.placeholder": "Nombra esta conversación…",
    "thread.edit": "Editar",
    "thread.done": "Listo",
    "thread.selectAll": "Seleccionar todo",
    "thread.deselectAll": "Deseleccionar todo",
    "thread.deleteSelected.confirm": "¿Eliminar las {n} conversaciones seleccionadas?",
    "thread.deleteBtn": "Eliminar",
    "thread.cancel": "Cancelar",
    "thread.count.one": "{n} conversación",
    "thread.count.many": "{n} conversaciones",
    "thread.streaming": "El agente está trabajando…",
    "resize.title": "Arrastra para ajustar · doble clic para restablecer",
    "addbot.chip": "Añadir un agente",
    "addbot.chipHelp":
      "Trae otro agente a esta conversación. Se convierte en un chat de grupo: este agente sigue siendo el que responde por defecto, los demás intervienen cuando los mencionas con @, y el historial reciente viaja con ella para que nadie tenga que ponerse al día a mano.",
    "history.show": "Mostrar historial de conversaciones",
    "history.hide": "Ocultar historial de conversaciones",
    "history.title": "Conversaciones",
    "history.close": "Cerrar el historial de conversaciones",
    "profile.show": "Mostrar el perfil del agente",
    "profile.hide": "Ocultar el perfil del agente",
    "thread.unread": "{n} nuevos del agente",
    "compose.send": "Enviar",
    "compose.stop": "Detener",
    "compose.queue": "En cola",
    "compose.queueTitle":
      "En cola: se envía cuando termine la respuesta actual (Enter hace lo mismo con teclado). Usa Intervenir para entregárselo al agente ahora.",
    "compose.steer": "Intervenir",
    "compose.steerTitle":
      "Intervenir: entrega esto al agente mientras sigue respondiendo. Lo lee en su siguiente paso, cuando termine la herramienta en curso, sin detenerse. Si la respuesta termina antes, se envía como tu siguiente mensaje.",
    "queue.label": "{n} en espera",
    "queue.kind.queue": "Se envía tras esta respuesta",
    "queue.kind.steer": "El agente lo lee en su siguiente paso, sin detenerse",
    "queue.steer":
      "Intervenir: el agente lo lee en su siguiente paso sin detenerse. Si la respuesta termina antes, se envía como tu siguiente mensaje.",
    "queue.steerAttachments": "Los mensajes con archivos no se pueden intervenir — este se envía tras la respuesta",
    "queue.steerIdle": "No hay nada en marcha — se envía como el siguiente mensaje",
    "queue.steered": "Intervenido — esperando el siguiente paso del agente",
    "queue.edit": "Editar: devolver esto al cuadro de mensaje",
    "queue.cancel": "Cancelar: no enviar esto",
    "queue.clear": "Cancelar todo",
    "msg.steered": "Intervención",
    "msg.steeredTitle": "Enviado mientras el agente trabajaba — lo leyó en su siguiente paso, a mitad de respuesta",
    "chat.thinking": "Pensando…",
    "chat.remote.working": "Trabajando en esto desde {channel}…",
    "chat.remote.workingUnknown": "Trabajando en esto desde otro dispositivo…",
    "chat.jumpLatest": "Ir a lo último",
    "toast.threadsFailed": "No se pudo cargar la lista de conversaciones.",
    "msg.copy": "Copiar",
    "msg.copied": "Copiado",
    "msg.copyFailed": "No se pudo copiar el mensaje.",
    "msg.edit": "Editar",
    "msg.editSend": "Enviar",
    "msg.editCancel": "Cancelar",
    "msg.time.now": "ahora mismo",
    "msg.time.min.one": "hace {n} min",
    "msg.time.min.many": "hace {n} min",
    "msg.time.hour.one": "hace {n} hora",
    "msg.time.hour.many": "hace {n} horas",
    "error.label": "Error",
    "error.fix": "Corregir",
    "error.askAdmin": "Esto necesita una clave o un inicio de sesión que solo puede configurar el administrador de tu espacio. Pídeselo; en cuanto lo conecte, vuelve a enviar tu mensaje.",
    "error.openDashboard": "Abrir el panel completo para arreglarlo",
    "error.newChat": "Empezar un chat nuevo",
    "error.newChatHint": "Esta conversación no puede continuar con el modelo configurado. Empieza un chat nuevo con este agente o vuelve a ponerle el modelo que usaba antes.",
    "error.codexName": "OpenAI Codex",
    "shell.placeholder": "Ejecuta un comando de shell…  (empieza con !)",
    "shell.mode": "Modo shell — se ejecuta en el workspace de {agent}, no se envía al agente",
    "shell.running": "Ejecutando…",
    "shell.error": "El comando de shell falló: {msg}",
    "shell.queued": "{n} resultado(s) de shell — se enviarán con tu próximo mensaje",
    "shell.sendNow": "Enviar al agente ahora",
    "shell.clear": "Borrar",
    "time.now": "ahora",
    "time.minutes": "hace {n} min",
    "time.hours": "hace {n} h",
    "time.days": "hace {n} d",
    "mode.title":
      "Aprobación de esta sesión — anula el perfil de Supervisión del agente. Auto: aprueba todo · Plan: planifica primero, aprueba pasos · Manual: pregunta antes de cada herramienta controlada. Clic para alternar.",
    "mode.auto": "Auto",
    "mode.plan": "Plan",
    "mode.manual": "Manual",
    "detail.title":
      "Detalle de herramientas y subagentes — Silencioso oculta las llamadas, Normal muestra una línea de actividad, Detallado muestra entrada/salida completa. Clic para alternar.",
    "detail.quiet": "Silencioso",
    "detail.normal": "Normal",
    "detail.verbose": "Detallado",
    "model.title": "Modelo de este chat — clic para cambiar",
    "model.none": "Modelo",
    "model.default": "Predeterminado del agente",
    "model.search": "Buscar modelos…",
    "model.empty": "No hay modelos disponibles",
    "model.unavailable":
      "Esta conversación usó por última vez {model}, que ya no está disponible aquí. Se continúa con el modelo predeterminado del agente.",
    "effort.title": "Esfuerzo de razonamiento de este chat — clic para cambiar",
    "effort.chip": "Esfuerzo",
    "effort.defaultLevel": "Predeterminado del agente ({level})",
    "effort.modelDefault": "Predeterminado del modelo",
    "context.title": "{used} / {total} tokens en contexto",
    "tokens.title": "Tokens de entrada ↑ / salida ↓ (esta conversación)",
    "calls.title": "Llamadas al modelo (esta conversación) — una por ida y vuelta, incluidos los bucles de herramientas",
    "toolCalls.title": "Llamadas a herramientas (esta conversación), incluidas las de MCP",
    "cost.title": "Costo estimado (esta conversación)",
    "cost.credits.title": "Créditos olano usados (esta conversación) \u2014 cobrados por la pasarela de Olano",
    "approval.needed": "Se requiere aprobación",
    "approval.blocked.one": "{n} acción bloqueada",
    "approval.blocked.many": "{n} acciones bloqueadas",
    "approval.acceptOnce": "Aceptar una vez",
    "approval.browserSendsTo": "El formulario se envía a {site}.",
    "approval.browserNote": "Aprueba solo si tú lo pediste: una página web puede intentar manipular a un agente.",
    "browser.handedBack": "Listo: te he devuelto el navegador.",
    "approval.acceptAll": "Aceptar todo",
    "approval.reject": "Rechazar",
    "approval.accepted": "Aceptado",
    "approval.acceptedAll": "Aceptado (todo en este turno)",
    "approval.rejected": "Rechazado",
    "approval.autoApproved": "Aprobado automáticamente",
    "approval.failed": "No se pudo enviar la aprobación.",
    "approval.expired": "Esta aprobación caducó mientras no estabas",
    "interrupted.chip": "Interrumpido",
    "interrupted.line": "Se perdió la conexión mientras {agent} respondía; la respuesta puede estar incompleta.",
    "interrupted.checking": "Buscando la respuesta completa…",
    "interrupted.retry": "Reintentar",
    "interrupted.retryTitle": "Enviar de nuevo tu último mensaje",
    "interrupted.recovered": "Se recuperó la respuesta completa",
    "reattach.chip": "Reconectando",
    "reattach.line": "{agent} siguió trabajando mientras no estabas; recuperando la respuesta.",
    "mode.set": "Modo cambiado a {mode}.",
    "plan.approveRun": "Aprobar y ejecutar",
    "plan.approveMsg": "Aprobado — ejecuta el plan ahora.",
    "mode.act": "Ejecutar",
    "slash.act": "Modo ejecutar — ejecuta ya, aprueba todo",
    "detail.set": "Detalle ajustado a {state}.",
    "activity.subagent": "Hablando con {name}",
    "activity.tool": "Llamando a {name}",
    "todo.title": "Plan",
    "todo.progress": "{done}/{total} hechas",
    "slash.heading": "Comandos de barra",
    "slash.unknown": "Comando desconocido: /{name}. Escribe /help.",
    "slash.help": "Mostrar los comandos disponibles",
    "slash.new": "Iniciar una conversación nueva",
    "slash.context": "Mostrar uso de contexto y tokens",
    "slash.compact": "Resumir y compactar esta conversación",
    "slash.quiet": "Ocultar todas las llamadas a herramientas",
    "slash.normal": "Mostrar una línea de actividad (predeterminado)",
    "slash.verbose": "Mostrar entrada y salida completas de herramientas",
    "slash.auto": "Modo auto — ejecuta sin preguntar",
    "slash.plan": "Modo plan — confirma antes de ejecutar",
    "slash.manual": "Modo manual — aprueba cada llamada",
    "slash.rename": "Renombrar la conversación actual",
    "slash.skills": "Listar las habilidades de este agente",
    "slash.subagents": "Listar los subagentes de este agente",
    "slash.tools": "Listar las herramientas de este agente",
    "slash.mcps": "Listar los servidores MCP de este agente",
    "slash.reloadSkills": "Recargar habilidades desde disco",
    "slash.reloadTools": "Recargar el catálogo de herramientas",
    "slash.reloadMcps": "Recargar servidores MCP",
    "slash.reloadAgent": "Recargar la configuración del agente desde disco y reconstruir",
    "slash.reloadVault": "Releer los secretos del baúl y reconstruir el agente",
    "slash.group.commands": "Comandos",
    "slash.group.skills": "Habilidades",
    "slash.customCommand": "Comando personalizado",
    "slash.skills.title": "Habilidades disponibles para {agent}",
    "slash.skills.none": "No hay habilidades disponibles.",
    "slash.skills.hint": "Escribe `/<habilidad> [tarea]` para invocar una.",
    "slash.subagents.title": "Subagentes de {agent}",
    "slash.subagents.none": "Este agente no tiene subagentes.",
    "slash.tools.title": "Herramientas disponibles para {agent}",
    "slash.tools.none": "No hay herramientas configuradas.",
    "slash.mcps.title": "Servidores MCP de {agent}",
    "slash.mcps.none": "Este agente no tiene servidores MCP.",
    "slash.mcps.disabled": "deshabilitado",
    "slash.mcps.status.connected": "conectado",
    "slash.mcps.status.authRequired": "requiere autorización",
    "slash.mcps.status.error": "error",
    "slash.mcps.tool": "{n} herramienta",
    "slash.mcps.tools": "{n} herramientas",
    "slash.mcps.notRunning": "El agente no está en ejecución — el estado refleja la configuración y los tokens guardados.",
    "mcp.statusTitle": "Estado de los servidores MCP — clic para ver detalles",
    "mcp.badge": "MCP {live}/{total}",
    "mcp.heading": "Servidores MCP",
    "mcp.setup": "Configurar →",
    "mcp.noServers": "No hay servidores MCP.",
    "reload.running": "Recargando…",
    "reload.failed": "Falló la recarga.",
    "reload.skills.done": "Se recargaron {n} habilidad(es).",
    "reload.tools.done": "Herramientas recargadas — {n} en este agente.",
    "reload.mcps.done": "Se recargaron {n} servidor(es) MCP.",
    "reload.agent.done": "Se recargó {agent} desde disco — cambios de configuración aplicados.",
    "reload.vault.done": "Baúl recargado — {n} secreto(s) disponibles; {agent} reconstruido.",
    "reload.vault.locked": "El baúl está bloqueado. Desbloquéalo y luego recarga.",
    "context.heading": "Uso de contexto",
    "context.model": "Modelo",
    "context.window": "Ventana",
    "context.estimated": "Uso estimado por categoría",
    "context.usage": "{used} / {total} tokens ({pct}%)",
    "context.usageNoWindow": "{used} tokens",
    "context.tokens": "tokens",
    "context.items": "{n} elementos",
    "context.onDemand": "carga bajo demanda",
    "context.cat.system_prompt": "Prompt del sistema",
    "context.cat.system_tools": "Herramientas del sistema",
    "context.cat.subagents": "Agentes personalizados",
    "context.cat.memory_files": "Archivos de memoria",
    "context.cat.skills": "Habilidades",
    "context.cat.mcp_tools": "Herramientas MCP",
    "context.cat.messages": "Mensajes",
    "context.cat.free": "Espacio libre",
    "compact.noThread": "No hay conversación activa para compactar.",
    "compact.running": "Compactando la conversación…",
    "compact.done": "Conversación compactada.",
    "compact.empty": "Nada que compactar todavía.",
    "compact.failed": "No se pudo compactar la conversación.",
    "rename.noThread": "No hay conversación activa para renombrar.",
    "rename.usage": "Uso: /rename <nuevo nombre>",
    "rename.done": "Renombrada a «{name}».",
    "rename.failed": "No se pudo renombrar la conversación.",
    "att.attach": "Adjuntar archivos",
    "att.drop": "Suelta para adjuntar",
    "att.tooLarge": "{name} supera los 10 MB.",
    "att.unreadable": "{name}: no se pudo leer el archivo. Guárdalo primero en tu dispositivo.",
    "att.remove": "Quitar adjunto",
    "att.download": "Descargar",
    "att.openInFiles": "Abrir en Archivos",
    "voice.hold": "Mantén pulsado para dictar · ⌘D / Ctrl+D para alternar",
    "voice.stop": "Dejar de dictar",
    "voice.listening": "Escuchando…",
    "voice.listeningHint": "suelta, o pulsa ⌘D / Ctrl+D, para parar",
    "voice.transcribing": "Transcribiendo…",
    "voice.denied": "Se bloqueó el acceso al micrófono — permítelo en los ajustes del sitio en tu navegador.",
    "voice.noSpeech": "No se entendió — inténtalo de nuevo.",
    "voice.network": "El reconocimiento de voz no está disponible — revisa tu conexión.",
    "voice.sttOff": "La transcripción está desactivada — actívala en Configuración → Voz.",
    "voice.error": "Falló la entrada por voz.",
    "toast.agentsFailed": "No se pudieron cargar los agentes.",
    "toast.openFailed": "No se pudo abrir la conversación.",
    "toast.deleteFailed": "No se pudo eliminar la conversación.",
    "toast.chatFailed": "Falló el chat.",
    "connect.title": "Conectar con {service}",
    "connect.magicTitle": "Conecta tu cuenta",
    "connect.button": "Conectar",
    "connect.pin": "PIN",
    "connect.hint": "Se abre en tu navegador — la conexión se completa aquí sola después de iniciar sesión.",
    "result.doneTitle": "Conectado",
    "result.failedTitle": "No se completó",
    "result.restarted": "Se reinició {agent}, así que el cambio ya está activo.",
    "result.reloaded": "Se recargaron las herramientas de {agent}; ya se pueden usar.",
    "result.restartNeeded": "{agent} necesita reiniciarse para aplicar esto.",
    "result.restartBtn": "Reiniciar {agent}",
    "result.restarting": "Reiniciando…",
    "result.restartDone": "{agent} reiniciado.",
    "result.restartFailed": "No se pudo reiniciar {agent}. Inténtalo desde su página.",
    "result.restartAsk": "Pide a un administrador que reinicie {agent} para aplicarlo.",
    "secret.loading": "Preparando el formulario seguro…",
    "secret.save": "Guardar en la bóveda",
    "secret.saving": "Guardando…",
    "secret.savedTitle": "Guardado en la bóveda",
    "secret.hint": "Va directo a la bóveda cifrada, nunca por este chat. El agente solo conoce el nombre.",
    "secret.spent": "Esta solicitud ya no está activa. Vuelve a pedírselo al agente si aún la necesitas.",
    "secret.failed": "No se pudo guardar. {detail}",
  },
};
